import { useEffect, useState } from 'react';

// Types
import { SchoolInfoForm } from './SchoolInfo.types';

// Redux
import onboarding, { OnboardingSlice } from '../../../OnboardingSlice';
import { useDispatch, useSelector } from 'react-redux';

// Services
import { postSchoolInfo } from 'services/Onboarding/General/useOnboarding';
import { useQueryClient } from 'react-query';

// React hook form
import { useForm } from 'react-hook-form';
import { useFinishOnboardingInfo } from 'services/Onboarding/General/Mutations/useFinishOnboardingInfo';
import { useParams } from 'react-router';
import { usePostSchoolInfo } from 'services/Onboarding/AdminOnboarding/Mutations/usePostSchoolInfo';
import { useAdminFinishOnboardingInfo } from 'services/Onboarding/AdminOnboarding/Mutations/useAdminFinishOnboardingInfo';
import { useRoles } from 'assets/utils/useRoles';

export const useLogic = () => {
  const { userId } = useParams();
  const { isAdmin } = useRoles();
  const { mutateAsync: adminPostPersonalInfo, isLoading: isLoadingAdminPostInfo } = usePostSchoolInfo();
  const { mutateAsync: finishOnboarding, isLoading: isLoadingFinishOnboarding } = useFinishOnboardingInfo();
  const { mutateAsync: adminFinishOnboarding, isLoading: isLoadingAdminFinishOnboarding } = useAdminFinishOnboardingInfo();
  const { nextPage, updateUserInfo, previousPage } = onboarding.actions;
  const dispatch = useDispatch();
  const onboardingState = useSelector((state: { onboarding: OnboardingSlice }) => state.onboarding);
  const [takenSat, setTakenSat] = useState<any>();

  const queryClient = useQueryClient();
  let promise1: any;
  let promise2: any;

  const { control, handleSubmit, formState: { errors }, watch, setValue, getValues } = useForm<SchoolInfoForm>({
    defaultValues: {
      graduationYear: undefined,
      takenCourse: {
        label: '',
        value: false
      },
      scores: {
        listening: 0,
        reading: 0,
        writing: 0,
        speaking: 0,
      }
    }
  });

  useEffect(() => {
    setTakenSat(watch('takenCourse.value'));
  }, [watch('takenCourse')]);

  const options = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];

  const roundToNearestMark = (x: number) => Math.round(x / 0.5) * 0.5;

  const getTotalScore = () => roundToNearestMark((Number(watch('scores.listening') || 0) + Number(watch('scores.reading') || 0) + Number(watch('scores.speaking') || 0) + Number(watch('scores.writing') || 0)) / 4);

  const formatDate = (birthday: any) => {
    const date = birthday.getDate();
    const month = birthday.getMonth() + 1;
    const year = birthday.getFullYear();
    return `${year}-${month < 10 ? `0${month}` : `${month}`}-${date < 10 ? `0${date}` : `${date}`}`;
  };

  const onSubmit = handleSubmit(async (data) => {
    if (errors.country?.label?.message || errors.schoolName?.label?.message || errors.graduationYear?.message || errors.scores?.listening?.message || errors.scores?.reading?.message || errors.scores?.writing?.message || errors.scores?.speaking?.message) return;
    if (userId) {
      promise1 = await adminPostPersonalInfo({
        userId: Number(userId),
        country: data.country.label,
        schoolName: data.schoolName?.label || '',
        graduationYear: formatDate(data.graduationYear),
        courseName: onboardingState.plan.course,
        stepId: onboardingState.activeStep,
        scores: {
          listening: Number(data.scores?.listening),
          reading: Number(data.scores?.reading),
          writing: Number(data.scores?.writing),
          speaking: Number(data.scores?.speaking),
        }
      });
    } else {
      promise2 = await postSchoolInfo({
        country: data.country.label,
        school_name: data.schoolName?.label || '',
        graduation_year: formatDate(data.graduationYear),
        course_name: onboardingState.plan.course,
        step_id: 3,
        scores: { ...data.scores, totalScore: getTotalScore() }
      });
    }
    dispatch(updateUserInfo({
      country: data.country.label,
      schoolName: data.schoolName?.label || '',
      graduationYear: formatDate(data.graduationYear),
    }));

    const promise3 = !isAdmin && finishOnboarding(onboardingState.plan.courseID);
    const promise4 = isAdmin && adminFinishOnboarding({
      userId: Number(userId),
      courseId: Number(onboardingState.plan.courseID),
    });
    queryClient.refetchQueries(['userinfo']);
    await Promise.any([promise1, promise2, promise3, promise4]).then(() => dispatch(nextPage()));
  });

  useEffect(() => {
    if (onboardingState.userInfo.country) { setValue('country', { label: onboardingState.userInfo.country, code: '' }); }
    if (onboardingState.userInfo.schoolName) { setValue('schoolName', { label: onboardingState.userInfo.schoolName, value: '' }); }
    if (onboardingState.userInfo.graduationYear) { setValue('graduationYear', new Date(onboardingState.userInfo.graduationYear || 0)); }
    if (onboardingState.userInfo.info?.done_courses?.IELTS.scores.listening) { setValue('scores.listening', onboardingState.userInfo.info.done_courses.IELTS.scores.listening); }
    if (onboardingState.userInfo.info?.done_courses?.IELTS.scores.reading) { setValue('scores.reading', onboardingState.userInfo.info.done_courses.IELTS.scores.reading); }
    if (onboardingState.userInfo.info?.done_courses?.IELTS.scores.writing) { setValue('scores.writing', onboardingState.userInfo.info.done_courses.IELTS.scores.writing); }
    if (onboardingState.userInfo.info?.done_courses?.IELTS.scores.speaking) { setValue('scores.speaking', onboardingState.userInfo.info.done_courses.IELTS.scores.speaking); }
  }, [onboardingState.userInfo]);

  return {
    options,
    watch,
    onSubmit,
    errors,
    control,
    takenSat,
    setTakenSat,
    onboardingState,
    setValue,
    getValues,
    getTotalScore,
    roundToNearestMark,
    isLoadingAdminFinishOnboarding,
    isLoadingFinishOnboarding,
    isLoadingAdminPostInfo,
    previousPage,
    dispatch
  };
};
